import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { channelsBadgeLabel } from '../../lib/campaignChannels';
import { campaignStatusVariant } from './campaignStatusVariant';
import { PinBadge } from '../../components/ui/PinBadge';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { CopyableLinkCell } from '../../components/ui/CopyableLinkCell';
import { StickyNav } from '../../components/ui/StickyNav';
import { DriveAssetsBrowser } from '../../components/drive/DriveAssetsBrowser';
import { useCurrentUser } from '../auth';
import { ACCOUNT_TYPE_LABEL } from '../../lib/accountTypeLabel';
import type { Account } from '../../types/account';
import type { Campaign, CampaignStatus, LandingPageOverride, LeadFormText } from '../../types/campaign';
import { getAccountDetail, listAccounts } from '../accounts/api';
import {
  changeCampaignStatus,
  clearAlternate,
  clearOptOut,
  deleteCampaign,
  designateAlternate,
  designateOptOut,
  getCampaign,
} from './api';
import { DeleteCampaignModal } from './DeleteCampaignModal';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-4 py-1.5">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900">{value ?? <span className="text-slate-400">—</span>}</dd>
    </div>
  );
}

function Section({
  id,
  title,
  children,
  actions,
}: {
  id: string;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section id={id} className="bg-white rounded-md border border-slate-200 p-6 scroll-mt-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function StatusBadge({ status }: { status: CampaignStatus }) {
  return <Badge variant={campaignStatusVariant(status)}>{status}</Badge>;
}

function leadFormTextDisplay(value: LeadFormText): string {
  if (value.mode === 'Generic') return 'Generic';
  return `Custom — ${value.text}`;
}

function landingOverrideDisplay(override: LandingPageOverride): string {
  if (override.type === 'inherit') return 'Inherits from Overview';
  if (override.type === 'custom') return `Custom — ${override.customUrl}`;
  return `Brand ${override.type} template`;
}

// Section nav anchors. Prefix `cd-` (Campaign Detail) keeps the ids
// distinct from CampaignForm's `cf-*`, RowEditor's `re-*`, and
// BuildView's `bv-*`. Each id is the DOM id on a <Section> rendered
// below; the StickyNav scroll-spy and click handlers anchor here.
// `cd-danger` is gated by `isAdmin` at render time — the SECTION_NAV
// constant lists every section, and the call site filters via the
// StickyNav `visible` flag below.
const SECTION_NAV: Array<{ id: string; label: string }> = [
  { id: 'cd-overview', label: 'Overview' },
  { id: 'cd-visibility', label: 'Visibility' },
  { id: 'cd-meta-naming', label: 'Meta naming' },
  // Pre-P2b-3 polish round 5 §2.4: Google naming section, parallel
  // to Meta naming.
  { id: 'cd-google-naming', label: 'Google naming' },
  { id: 'cd-meta', label: 'Meta ad copy' },
  { id: 'cd-google', label: 'Google assets' },
  { id: 'cd-assets', label: 'Asset folders' },
  { id: 'cd-utm', label: 'UTM tags' },
  { id: 'cd-lifecycle', label: 'Lifecycle' },
  { id: 'cd-danger', label: 'Danger zone' },
];

const ALL_STATUSES: CampaignStatus[] = ['draft', 'active', 'inactive', 'archived'];

export function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const campaignQuery = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => getCampaign(id!),
    enabled: !!id,
  });

  const brandId = campaignQuery.data?.createdInBrandAccountId;
  const brandQuery = useQuery({
    queryKey: ['account', brandId],
    queryFn: () => getAccountDetail(brandId!),
    enabled: !!brandId,
  });

  // Pre-P2b-3 §5: the Visibility section resolves shareTarget accountIds
  // to display names + types. listAccounts is the same query other
  // surfaces use, so the cache is already warm in most navigation flows.
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
  });
  const accountsById = useMemo(() => {
    const map = new Map<string, Account>();
    for (const a of accountsQuery.data ?? []) map.set(a.id, a);
    return map;
  }, [accountsQuery.data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  };

  const statusMutation = useMutation({
    mutationFn: (status: CampaignStatus) => changeCampaignStatus(id!, status),
    onSuccess: invalidate,
    onError: (e: Error) => setActionError(e.message),
  });

  const designateAlternateMut = useMutation({
    mutationFn: () => designateAlternate(id!),
    onSuccess: invalidate,
    onError: (e: Error) => setActionError(e.message),
  });
  const designateOptOutMut = useMutation({
    mutationFn: () => designateOptOut(id!),
    onSuccess: invalidate,
    onError: (e: Error) => setActionError(e.message),
  });
  const clearAlternateMut = useMutation({
    mutationFn: () => clearAlternate(id!),
    onSuccess: invalidate,
    onError: (e: Error) => setActionError(e.message),
  });
  const clearOptOutMut = useMutation({
    mutationFn: () => clearOptOut(id!),
    onSuccess: invalidate,
    onError: (e: Error) => setActionError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCampaign(id!),
    onSuccess: () => {
      setDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      navigate('/campaigns');
    },
  });

  if (campaignQuery.isLoading) return <CenteredSpinner />;
  if (campaignQuery.error || !campaignQuery.data) {
    return (
      <div className="text-red-600 text-sm">
        Campaign not found or failed to load.
      </div>
    );
  }

  const c = campaignQuery.data;
  const brand = brandQuery.data?.account ?? null;
  const isAdmin = user.role === 'admin';

  return (
    <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-8 max-w-5xl mx-auto">
      {/* Round 3 (Fix 1): replaced the local sticky <nav> with the
          shared StickyNav primitive — adds scroll-spy that the
          previous inline implementation lacked, matching the
          treatment Build View / RowEditor / CampaignForm already
          got. cd-danger is gated by isAdmin so non-admins don't see
          a menu link to a section that didn't render; SECTION_NAV
          itself stays admin-agnostic for symmetry. No actions slot
          — CampaignDetail is the read-only view, edits live on the
          /edit route via CampaignForm. */}
      <StickyNav
        ariaLabel="Campaign Detail sections"
        sections={SECTION_NAV.map((s) =>
          s.id === 'cd-danger' ? { ...s, visible: isAdmin } : s,
        )}
      />
      <div className="min-w-0 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-slate-500 mb-1">
              {brand ? (
                <Link to={`/accounts/${brand.id}`} className="hover:underline">
                  {brand.name}
                </Link>
              ) : (
                <span>{c.createdInBrandAccountId}</span>
              )}
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">{c.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <StatusBadge status={c.status} />
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                {channelsBadgeLabel(c.channels)}
              </span>
              {c.isAlternate && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                  Alternate
                </span>
              )}
              {c.isOptOut && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-rose-100 text-rose-700">
                  Opt Out
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Pre-P2b-3 polish (Fix 6): Duplicate seeds CampaignNew via
                location.state with the full source Campaign. AM tweaks
                + provides a unique slug before saving. Designations
                (isAlternate / isOptOut) are NEVER copied. */}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate('/campaigns/new', { state: { duplicateFrom: c } })
              }
              title="Create a new Campaign with the same content + share scope"
            >
              📋 Duplicate
            </Button>
            <Link to={`/campaigns/${c.id}/edit`}>
              <Button type="button">Edit</Button>
            </Link>
          </div>
        </div>

        {actionError && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {actionError}
          </div>
        )}

        <Section id="cd-overview" title="Overview">
          <dl>
            <Field label="Slug" value={<code className="font-mono text-xs">{c.slug}</code>} />
            <Field label="Description" value={c.description} />
            {/* Pre-P2b-3 polish round 5 §2.4: descriptor moved here
                from the Meta Naming section. Shared between Meta
                naming and Google ad-labels — single value at the
                Campaign Overview level. */}
            <Field label="Campaign descriptor" value={c.campaignDescriptor ?? null} />
            {/* Consolidation §5.4: Offer Mechanics + Kyck Campaign
                Overview URLs removed from Campaign — they live on the
                Promotion Plan now. */}
            <Field label="Landing page type" value={c.landingPageType} />
            {c.landingPageType === 'custom' && (
              <Field
                label="Custom landing URL"
                value={
                  c.customLandingPageUrl ? (
                    <code className="font-mono text-xs break-all">{c.customLandingPageUrl}</code>
                  ) : null
                }
              />
            )}
            <Field label="Created by" value={c.createdBy} />
          </dl>
        </Section>

        <Section id="cd-visibility" title="Visibility">
          {c.shareTargets.length === 0 ? (
            <p className="text-sm text-amber-700">
              No share targets — this Campaign is invisible from every dropdown
              until at least one target is added.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {c.shareTargets.map((t) => {
                const acc = accountsById.get(t.accountId);
                if (!acc) {
                  return (
                    <li key={t.accountId} className="text-slate-500">
                      <span className="text-xs text-slate-400">(unknown)</span>{' '}
                      <code className="font-mono text-xs">{t.accountId}</code>
                    </li>
                  );
                }
                return (
                  <li key={t.accountId} className="text-slate-800">
                    <span className="text-xs text-slate-500 mr-2">
                      {ACCOUNT_TYPE_LABEL[acc.accountType] ?? acc.accountType}:
                    </span>
                    <Link to={`/accounts/${acc.id}`} className="hover:underline">
                      {acc.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Pre-P2b-3 polish round 5 §2.4: Meta naming now holds just
            the per-Campaign template override; descriptor moved up to
            Overview (shared with Google labels). */}
        <Section id="cd-meta-naming" title="Meta naming">
          <dl>
            <Field
              label="Per-Campaign template override"
              value={
                c.metaCampaignNameTemplate ? (
                  <code className="font-mono text-xs break-all">
                    {c.metaCampaignNameTemplate}
                  </code>
                ) : (
                  <span className="text-slate-400 italic">
                    Inherits brand default
                  </span>
                )
              }
            />
          </dl>
        </Section>

        {/* Pre-P2b-3 polish round 5 §2.2 + §2.4: Google ad-label
            template, parallel to Meta naming. */}
        <Section id="cd-google-naming" title="Google naming">
          <dl>
            <Field
              label="Per-Campaign template override"
              value={
                c.googleAdLabelTemplate ? (
                  <code className="font-mono text-xs break-all">
                    {c.googleAdLabelTemplate}
                  </code>
                ) : (
                  <span className="text-slate-400 italic">
                    Inherits brand default
                  </span>
                )
              }
            />
          </dl>
        </Section>

        <Section id="cd-meta" title="Meta ad copy">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Body copy ({c.metaAdCopy.bodyCopy.length})</h3>
          <ol className="space-y-2 list-decimal list-inside mb-4">
            {c.metaAdCopy.bodyCopy.map((v, i) => (
              <li key={i} className="text-sm text-slate-800 whitespace-pre-wrap">{v}</li>
            ))}
            {c.metaAdCopy.bodyCopy.length === 0 && (
              <li className="text-sm text-slate-400 italic list-none">No body copy authored yet.</li>
            )}
          </ol>

          <h3 className="text-sm font-semibold text-slate-900 mb-2">Headlines ({c.metaAdCopy.headlines.length})</h3>
          <ol className="space-y-1 list-decimal list-inside mb-4">
            {c.metaAdCopy.headlines.map((v, i) => (
              <li key={i} className="text-sm text-slate-800">{v}</li>
            ))}
            {c.metaAdCopy.headlines.length === 0 && (
              <li className="text-sm text-slate-400 italic list-none">No headlines authored yet.</li>
            )}
          </ol>

          <h3 className="text-sm font-semibold text-slate-900 mb-2">Descriptions ({c.metaAdCopy.descriptions.length})</h3>
          <ol className="space-y-1 list-decimal list-inside mb-4">
            {c.metaAdCopy.descriptions.map((v, i) => (
              <li key={i} className="text-sm text-slate-800">{v}</li>
            ))}
            {c.metaAdCopy.descriptions.length === 0 && (
              <li className="text-sm text-slate-400 italic list-none">No descriptions (optional).</li>
            )}
          </ol>

          <dl>
            <Field
              label="Meta ad type"
              value={
                c.metaAdCopy.metaAdType === 'traffic'
                  ? 'Website Lead'
                  : c.metaAdCopy.metaAdType === 'lead_gen'
                    ? 'Meta Lead Gen'
                    : c.metaAdCopy.metaAdType === 'both'
                      ? 'Both'
                      : c.metaAdCopy.metaAdType
              }
            />
            <Field label="CTA button" value={c.metaAdCopy.ctaButton} />
            {(c.metaAdCopy.metaAdType === 'traffic' || c.metaAdCopy.metaAdType === 'both') && (
              <Field
                label="Meta landing override"
                value={landingOverrideDisplay(c.metaLandingPageOverride ?? { type: 'inherit' })}
              />
            )}
          </dl>
        </Section>

        {/* Multi Lead Form spec §1.1. Lead forms moved off MetaAdCopy onto
            top-level Campaign.campaignLeadForms[]; a Campaign now holds an
            authored library of named forms. Detail view lists each entry
            with its name + formType + the four authored text fields,
            badging the one nominated as default. Lead-form-only rendering
            doesn't gate on metaAdType — even traffic-only Campaigns can
            carry forms (skeletons / future use), so the section always
            renders. Tab strip authoring lives on the editor; this is a
            read-mode summary. */}
        <Section id="cd-lead-forms" title={`Lead forms (${c.campaignLeadForms?.length ?? 0})`}>
          {(c.campaignLeadForms ?? []).length === 0 ? (
            <p className="text-sm text-slate-400 italic">No lead forms authored yet.</p>
          ) : (
            <div className="space-y-4">
              {(c.campaignLeadForms ?? []).map((lf) => (
                <div
                  key={lf.id}
                  className="rounded-md border border-slate-200 bg-white p-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-slate-900">{lf.name}</span>
                    {lf.id === c.defaultLeadFormId && (
                      <span className="inline-flex items-center rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                        default
                      </span>
                    )}
                  </div>
                  <dl>
                    <Field label="Form type" value={lf.formType} />
                    <Field label="Headline" value={leadFormTextDisplay(lf.fields.headline)} />
                    <Field label="Description" value={leadFormTextDisplay(lf.fields.description)} />
                    <Field label="Thank-you headline" value={leadFormTextDisplay(lf.fields.thankYouHeadline)} />
                    <Field
                      label="Thank-you description"
                      value={leadFormTextDisplay(lf.fields.thankYouDescription)}
                    />
                  </dl>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section id="cd-google" title="Google assets">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Headlines ({c.googleAssets.headlines.length})</h3>
          <ol className="space-y-1 list-decimal list-inside mb-4">
            {c.googleAssets.headlines.map((h, i) => (
              <li key={i} className="text-sm text-slate-800">
                <span className="inline-flex items-center gap-1.5 align-middle">
                  <span>{h.text}</span>
                  <PinBadge pinPosition={h.pinPosition} />
                </span>
              </li>
            ))}
            {c.googleAssets.headlines.length === 0 && (
              <li className="text-sm text-slate-400 italic list-none">No headlines authored yet.</li>
            )}
          </ol>
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Descriptions ({c.googleAssets.descriptions.length})</h3>
          <ol className="space-y-1 list-decimal list-inside mb-4">
            {c.googleAssets.descriptions.map((v, i) => (
              <li key={i} className="text-sm text-slate-800">{v}</li>
            ))}
            {c.googleAssets.descriptions.length === 0 && (
              <li className="text-sm text-slate-400 italic list-none">No descriptions authored yet.</li>
            )}
          </ol>
          <dl>
            <Field label="Path 1" value={<code className="font-mono text-xs">{c.googleAssets.path1 || '—'}</code>} />
            <Field
              label="Path 2"
              value={c.googleAssets.path2 ? <code className="font-mono text-xs">{c.googleAssets.path2}</code> : null}
            />
            <Field
              label="Google landing override"
              value={landingOverrideDisplay(c.googleLandingPageOverride ?? { type: 'inherit' })}
            />
          </dl>
          <p className="text-xs text-slate-500 mt-2">
            The Google final URL is derived from the landing override + UTM config at rollout
            snapshot (P2b). Nothing to author here.
          </p>
        </Section>

        <Section id="cd-assets" title="Asset folders">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-500 mb-2">Meta assets folder</p>
              {c.metaAssetsFolderUrl ? (
                <p className="mb-2 text-xs text-slate-500">
                  <a
                    href={c.metaAssetsFolderUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {c.metaAssetsFolderUrl}
                  </a>
                </p>
              ) : null}
              <DriveAssetsBrowser
                mode="view"
                title="Meta Assets"
                folderUrl={c.metaAssetsFolderUrl ?? ''}
                value={c.metaAssetLabels ?? []}
                // Ad Preview Mockups Stage 1 §2.3: read-only badge of
                // each asset's classification on the Campaign detail page.
                showAdType
              />
            </div>
            <dl>
              <Field
                label="Google assets folder"
                value={
                  c.googleAssetsFolderUrl ? (
                    <a href={c.googleAssetsFolderUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
                      {c.googleAssetsFolderUrl}
                    </a>
                  ) : null
                }
              />
              <Field
                label="Other assets folder"
                value={
                  c.otherAssetsFolderUrl ? (
                    <a href={c.otherAssetsFolderUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
                      {c.otherAssetsFolderUrl}
                    </a>
                  ) : null
                }
              />
            </dl>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            The Meta folder is browsable inline (Phase 1 of P2c). The Google and Other folders
            remain plain link passthroughs.
          </p>
        </Section>

        <Section id="cd-utm" title="UTM tags">
          {!c.utmConfig?.enabled ? (
            <p className="text-sm text-slate-500">
              UTM appending is disabled. Resolved landing URLs are shipped without UTM params at
              rollout snapshot.
            </p>
          ) : (
            <dl>
              <Field
                label="Platforms"
                value={
                  c.utmConfig.platforms.length === 0
                    ? '(none selected)'
                    : c.utmConfig.platforms.join(', ')
                }
              />
              <Field label="utm_campaign (id)" value={c.utmConfig.shared.campaignId} />
              <Field label="Campaign name" value={c.utmConfig.shared.campaignName} />
              {c.utmConfig.platforms.includes('meta') && c.utmConfig.meta && (
                <>
                  <Field label="Meta source" value={c.utmConfig.meta.source || '—'} />
                  <Field label="Meta medium" value={c.utmConfig.meta.medium || '—'} />
                  <Field label="Meta term" value={c.utmConfig.meta.term || '—'} />
                  <Field label="Meta content" value={c.utmConfig.meta.content || '—'} />
                </>
              )}
              {c.utmConfig.platforms.includes('google') && c.utmConfig.google && (
                <>
                  <Field label="Google source" value={c.utmConfig.google.source || '—'} />
                  <Field label="Google medium" value={c.utmConfig.google.medium || '—'} />
                  <Field label="Google term" value={c.utmConfig.google.term || '—'} />
                  <Field label="Google content" value={c.utmConfig.google.content || '—'} />
                </>
              )}
            </dl>
          )}
        </Section>

        <Section id="cd-lifecycle" title="Lifecycle">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Status</h3>
              <div className="flex flex-wrap gap-2">
                {ALL_STATUSES.map((s) => (
                  <Button
                    key={s}
                    type="button"
                    variant={c.status === s ? 'default' : 'outline'}
                    onClick={() => statusMutation.mutate(s)}
                    disabled={statusMutation.isPending || c.status === s}
                  >
                    {s}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Archived campaigns stay fully editable (per spec). Only 'active' appears in the
                rollout picker.
              </p>
            </div>

            {isAdmin && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Designation</h3>
                <div className="flex flex-wrap gap-2">
                  {c.isAlternate ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => clearAlternateMut.mutate()}
                      disabled={clearAlternateMut.isPending}
                    >
                      Clear Alternate
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => designateAlternateMut.mutate()}
                      disabled={designateAlternateMut.isPending || c.isOptOut}
                    >
                      Designate as Alternate
                    </Button>
                  )}
                  {c.isOptOut ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => clearOptOutMut.mutate()}
                      disabled={clearOptOutMut.isPending}
                    >
                      Clear Opt Out
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => designateOptOutMut.mutate()}
                      disabled={designateOptOutMut.isPending || c.isAlternate}
                    >
                      Designate as Opt Out
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Exactly one Alternate + one Opt Out per Brand. Clashing designations are rejected;
                  clear the existing one first.
                </p>
              </div>
            )}
          </div>
        </Section>

        {isAdmin && (
          <Section id="cd-danger" title="Danger zone">
            <div className="border border-red-200 rounded-md p-4 bg-red-50 space-y-3">
              <p className="text-sm text-red-800">
                Deleting a Campaign removes it permanently. This can't be undone. Archive it instead
                if you want to keep history.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteOpen(true)}
                disabled={c.isAlternate || c.isOptOut}
              >
                Delete permanently
              </Button>
              {(c.isAlternate || c.isOptOut) && (
                <p className="text-xs text-red-700">
                  This Campaign is the Brand's designated{' '}
                  {c.isAlternate ? 'Alternate' : 'Opt Out'}. Clear the designation above before
                  deleting (so the Brand keeps its required pair).
                </p>
              )}
            </div>
          </Section>
        )}

        {deleteOpen && (
          <DeleteCampaignModal
            campaign={c}
            onClose={() => setDeleteOpen(false)}
            onConfirm={async () => {
              await deleteMutation.mutateAsync();
            }}
          />
        )}
      </div>
    </div>
  );
}
